import React from 'react';
import {
  FileText, Download, BookOpen, FileCode2, BarChart3, Shield,
  ExternalLink, Lock, ArrowRight
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const ResourceCard = ({ resource, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation({ threshold: 0.1 });

  const iconMap = {
    technical: FileCode2,
    market: BarChart3,
    compliance: Shield,
    guide: BookOpen
  };

  const Icon = iconMap[resource.type] || FileText;

  return (
    <div
      ref={cardRef}
      className={`group transition-all duration-500 ${
        cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="h-full p-6 rounded-2xl glass-card border border-white/10 hover:border-cyan-500/30 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-cyan-500/10">
        {/* Icon and Badge */}
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl ${resource.color} flex items-center justify-center`}>
            <Icon className="w-6 h-6" />
          </div>
          {resource.badge && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${resource.badgeColor}`}>
              {resource.badge}
            </span>
          )}
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
          {resource.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
          {resource.description}
        </p>

        {/* Key Points */}
        {resource.points && (
          <ul className="space-y-1.5 mb-4">
            {resource.points.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                <ArrowRight className="w-3 h-3 text-cyan-400 mt-1 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        <div className="mt-auto pt-4 border-t border-white/5">
          {resource.gated ? (
            <a
              href="#contact"
              className="inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <Lock className="w-4 h-4" />
              Request Access
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <a
              href={resource.link}
              className="inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <Download className="w-4 h-4" />
              {resource.ctaText || "Download PDF"}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Resources = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [highlightRef, highlightVisible] = useScrollAnimation({ threshold: 0.2 });

  const resources = [
    {
      type: "technical",
      title: "Technical Architecture Whitepaper",
      description: "Deep dive into ASTRA's two-phase architecture, the Hot Path Engine, proprietary ring buffer implementation, and how we achieve ~2ns amortized decision latency.",
      points: [
        "Hot Path vs Async Path design",
        "Lock-free cache implementation",
        "Memory barrier and torn-read detection",
        "Codon compiler optimizations"
      ],
      color: "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30",
      badge: "Technical",
      badgeColor: "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30",
      gated: true
    },
    {
      type: "market",
      title: "Global Market Gap Analysis",
      description: "Analysis of the 5,000-8,000 trading institutions caught between expensive Tier 1 solutions and inadequate Tier 2 offerings. Where ASTRA fits.",
      points: [
        "Market size and opportunity",
        "Competitive landscape analysis",
        "Target customer profiles",
        "Go-to-market strategy"
      ],
      color: "bg-purple-500/20 text-purple-400 border border-purple-500/30",
      badge: "Market Analysis",
      badgeColor: "bg-purple-500/20 text-purple-400 border border-purple-500/30",
      gated: true
    },
    {
      type: "compliance",
      title: "Compliance & Regulatory Guide",
      description: "Comprehensive guide to ASTRA's MiFID II RTS 6/25, SEC Rule 17a-4, and anti-manipulation implementations. How we handle regulatory requirements.",
      points: [
        "MiFID II RTS 6 algorithm controls",
        "RTS 25 order tagging implementation",
        "SEC WORM-compliant audit trails",
        "Anti-manipulation detection algorithms"
      ],
      color: "bg-green-500/20 text-green-400 border border-green-500/30",
      badge: "Compliance",
      badgeColor: "bg-green-500/20 text-green-400 border border-green-500/30",
      gated: true
    },
    {
      type: "guide",
      title: "Investor Technical Brief",
      description: "Executive summary of ASTRA's technology stack, competitive advantages, and roadmap. Designed for technical due diligence.",
      points: [
        "Performance claims verified",
        "Technology stack overview",
        "Competitive moat analysis",
        "Production deployment roadmap"
      ],
      color: "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30",
      badge: "Investor",
      badgeColor: "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30",
      gated: true
    }
  ];

  const highlights = [
    { value: "15,000+", label: "Lines of Code Documented" },
    { value: "6", label: "Protocol Specifications" },
    { value: "50+", label: "Configuration Options" },
    { value: "12+", label: "Risk Control Parameters" }
  ];

  return (
    <section id="resources" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
            <BookOpen className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-400">Due Diligence Materials</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Technical <span className="text-gradient">Documentation</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Comprehensive technical materials for investors and partners.
            Understand the breakthrough technology behind 950M+ decisions/sec performance.
          </p>
        </div>

        {/* Highlight Stats */}
        <div
          ref={highlightRef}
          className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12 transition-all duration-700 ${
            highlightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {highlights.map((item, index) => (
            <div
              key={index}
              className="p-4 rounded-xl glass-card text-center"
            >
              <div className="text-2xl font-bold text-cyan-400">{item.value}</div>
              <div className="text-sm text-muted-foreground">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Resource Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {resources.map((resource, index) => (
            <ResourceCard key={index} resource={resource} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl glass-card">
            <div className="text-center sm:text-left">
              <p className="text-white font-medium mb-1">
                Interested in technical due diligence?
              </p>
              <p className="text-sm text-muted-foreground">
                Contact us for full documentation access and technical briefings.
              </p>
            </div>
            <a
              href="#contact"
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-cyan-500/25 transition-all whitespace-nowrap"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resources;
